import { Check, Loader, X } from 'lucide-react'
import { H2, Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type StepStatus = 'pending' | 'active' | 'complete' | 'error'

export type ProgressStep = {
  id: string
  label: string
}

type ProgressViewProps = {
  steps: ProgressStep[]
  statuses: Record<string, StepStatus>
  errorMessage: string | null
  onRetry: () => void
}

export const ProgressView = ({
  steps,
  statuses,
  errorMessage,
  onRetry,
}: ProgressViewProps) => {
  const hasError = errorMessage !== null

  return (
    <div className="w-full">
      <div className="mb-8">
        <H2 className="text-pretty">
          {hasError ? 'Something went wrong' : 'Setting things up'}
        </H2>
        <p className="mt-2 text-muted-foreground text-pretty">
          {hasError
            ? 'One of the steps below didn\u2019t complete. You can try again.'
            : 'Hang tight \u2014 we\u2019re finishing your account setup.'}
        </p>
      </div>
      <ol>
        {steps.map((step, index) => {
          const status = statuses[step.id] ?? 'pending'
          const isLast = index === steps.length - 1
          const showError = status === 'error' && errorMessage !== null
          return (
            <li
              key={step.id}
              aria-current={status === 'active' ? 'step' : undefined}
              className="flex gap-4"
            >
              <div className="flex flex-col items-center">
                <StepIcon status={status} index={index} />
                {!isLast ? (
                  <div
                    className={cn(
                      'w-px flex-1 min-h-4 my-1',
                      status === 'complete' ? 'bg-primary' : 'bg-border'
                    )}
                  />
                ) : null}
              </div>
              <div className={cn('flex flex-col gap-1', !isLast && 'pb-6')}>
                <div
                  className={cn(
                    'font-medium',
                    status === 'pending' && 'text-muted-foreground',
                    status === 'error' && 'text-destructive'
                  )}
                >
                  {step.label}
                </div>
                {status === 'active' ? <Muted>In progress&#8230;</Muted> : null}
                {showError ? (
                  <p className="text-sm text-destructive">{errorMessage}</p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>
      {hasError ? (
        <Button
          type="button"
          variant="outline"
          className="mt-6 w-full"
          onClick={onRetry}
        >
          Try again
        </Button>
      ) : null}
    </div>
  )
}

const StepIcon = ({ status, index }: { status: StepStatus; index: number }) => {
  if (status === 'complete') {
    return (
      <div className="size-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
        <Check className="size-4" aria-hidden="true" />
      </div>
    )
  }
  if (status === 'active') {
    return (
      <div className="size-8 rounded-full border-2 border-primary text-primary bg-background flex items-center justify-center shrink-0">
        <Loader className="size-4 animate-spin" aria-hidden="true" />
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className="size-8 rounded-full bg-destructive text-white flex items-center justify-center shrink-0">
        <X className="size-4" aria-hidden="true" />
      </div>
    )
  }
  return (
    <div className="size-8 rounded-full border-2 border-muted text-muted-foreground bg-background flex items-center justify-center shrink-0 text-sm">
      {index + 1}
    </div>
  )
}
